/**
 * Where a thing comes from and what it is for.
 *
 * The skill guide answers "what does this skill open", which is the question
 * somebody asks when they are choosing what to train. This answers the other
 * one, which is the question somebody asks holding an unfamiliar item: where do
 * I get another, and what is it good for.
 *
 * Worked out from the definitions the client already holds, so anything written
 * in the tools turns up the moment it is saved, and nothing here needs a round
 * trip to ask.
 *
 * Its own file rather than sitting beside the window that draws it, so the
 * window is only a window.
 */

import type { ItemDefinitionWire, ObjectDefinitionWire, RecipeWire } from '@inverse/protocol';

/** One way of making something, said the way somebody would read it out. */
export interface Making {
  /**
   * What comes out, counting the quantity: "5 x Bronze Arrow".
   *
   * Empty where nothing comes out at all, which is what burning a log is: the
   * recipe names no item, and writing that out as the item numbered nought put
   * "Pine Log to Item 0" in the book.
   */
  gives: string;
  /** What is done, for the ones that make nothing: Burn, Light, Bury. */
  action: string;
  /** What goes in, the same way. */
  takes: string;
  skill: string;
  level: number;
  /** Everywhere it can be done, or empty for anything done in the bag. */
  where: string;
  experience: number;
  /** Anything beyond a level: other skills, a quest, something done first. */
  alsoNeeds: string[];
}

/** Everything the client can say about one item. */
export interface Lookup {
  id: number;
  name: string;
  examine: string;
  /** Recipes that produce it. */
  madeBy: Making[];
  /** Recipes that consume it. */
  usedIn: Making[];
  /** Scenery it is gathered from, by name. */
  gatheredFrom: string[];
  /** The level that gathering asks for, where it is gathered at all. */
  gatherLevel: number;
  /** What it sells for, which is the only price the client knows. */
  value: number;
  /** What sort of thing it is, which is the word the bank's tabs sort by. */
  kind: string;
}

export interface Shelf {
  items: readonly ItemDefinitionWire[];
  objects: readonly ObjectDefinitionWire[];
  recipes: readonly RecipeWire[];
  yields: readonly { objectId: number; itemId: number }[];
  quests: readonly { id: string; name: string }[];
}

/**
 * One line of a recipe, written out.
 *
 * Hidden recipes are left out by the caller rather than here: whether a surprise
 * should be readable is a decision about the guide, and this is the sentence.
 */
function makingOf(recipe: RecipeWire, shelf: Shelf): Making {
  const named = (itemId: number): string =>
    shelf.items.find((item) => item.id === itemId)?.name ?? `Item ${itemId}`;
  const place = (objectId: number): string =>
    shelf.objects.find((object) => object.id === objectId)?.name ?? '';

  const counted = (many: number, itemId: number): string =>
    many > 1 ? `${many} x ${named(itemId)}` : named(itemId);

  const alsoNeeds = recipe.alsoNeeds.map((also) => `${also.skill} ${also.level}`);

  if (recipe.locked?.quest) {
    const quest = shelf.quests.find((one) => one.id === recipe.locked?.quest);
    alsoNeeds.push(quest ? `Quest: ${quest.name}` : 'A quest');
  }
  if (recipe.locked?.flag) alsoNeeds.push('Something done first');

  // Everywhere it can be made, because the same thing is often made in more than
  // one place and naming one of them sends people to the wrong side of the map.
  const places = [recipe.objectId, ...recipe.alsoAt].map(place).filter((one) => one.length > 0);

  return {
    gives: recipe.outputId > 0 ? counted(recipe.outputQuantity, recipe.outputId) : '',
    action: recipe.action,
    takes: counted(recipe.inputQuantity, recipe.inputId),
    skill: recipe.skill,
    level: recipe.level,
    where: [...new Set(places)].join(' or '),
    experience: recipe.experience,
    alsoNeeds,
  };
}

/** Everything that can be said about one item, or nothing if there is no such item. */
export function lookUp(itemId: number, shelf: Shelf): Lookup | null {
  const item = shelf.items.find((one) => one.id === itemId);
  if (!item) return null;

  const open = shelf.recipes.filter((recipe) => !recipe.hidden);

  const gatheredFrom = [
    ...new Set(
      shelf.yields
        .filter((pair) => pair.itemId === itemId)
        .map((pair) => shelf.objects.find((object) => object.id === pair.objectId)?.name ?? '')
        .filter((one) => one.length > 0),
    ),
  ].sort((a, b) => a.localeCompare(b));

  const byLevel = (a: Making, b: Making): number =>
    a.level - b.level || (a.gives || a.action).localeCompare(b.gives || b.action);

  return {
    id: item.id,
    name: item.name,
    examine: item.examine,
    madeBy: open
      .filter((recipe) => recipe.outputId === itemId)
      .map((recipe) => makingOf(recipe, shelf))
      .sort(byLevel),
    usedIn: open
      .filter((recipe) => recipe.inputId === itemId)
      .map((recipe) => makingOf(recipe, shelf))
      .sort(byLevel),
    gatheredFrom,
    gatherLevel: item.gatherLevel,
    value: item.value,
    kind: item.kind,
  };
}

/**
 * Items whose name matches what somebody typed, best first.
 *
 * A name that starts with what was typed comes before one that merely contains
 * it, so typing "bro" finds Bronze Bar before Iron Bar's bronze-tipped cousin.
 * Nothing typed gives nothing back rather than the whole catalogue: a list of
 * every item in the world is not an answer to a question nobody asked yet.
 */
export function itemsLike(
  typed: string,
  items: readonly ItemDefinitionWire[],
  most = 24,
): ItemDefinitionWire[] {
  const wanted = typed.trim().toLowerCase();
  if (wanted.length === 0) return [];

  const found = items.filter((item) => item.name.toLowerCase().includes(wanted));

  return found
    .sort((a, b) => {
      const first = a.name.toLowerCase().startsWith(wanted) ? 0 : 1;
      const second = b.name.toLowerCase().startsWith(wanted) ? 0 : 1;
      return first - second || a.name.localeCompare(b.name);
    })
    .slice(0, most);
}
